import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/Toast'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Sheet } from '@/components/ui/Sheet'
import { DESTINATION_LABELS, KIND_LABELS, createFund, renameFund, setFundArchived } from '@/data/income'
import type { IncomeDestination, IncomeFund, IncomeKind } from '@/data/income'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')
const NEW_KINDS: Exclude<IncomeKind, 'tithe'>[] = ['offering', 'special_offering', 'other']

/** Add, rename and retire funds. What a fund IS (its kind and where the money goes) cannot change later. */
export function FundsPanel({ funds, isOpen, onClose }: { funds: IncomeFund[]; isOpen: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [kind, setKind] = useState<Exclude<IncomeKind, 'tithe'>>('special_offering')
  const [destination, setDestination] = useState<IncomeDestination>('local')
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const [error, setError] = useState('')
  const refresh = () => qc.invalidateQueries({ queryKey: ['income'] })

  const toast = useToast()
  const add = useMutation({ mutationFn: () => createFund({ name, kind, destination }) })
  const rename = useMutation({ mutationFn: (r: { id: string; name: string }) => renameFund(r.id, r.name) })
  const archive = useMutation({ mutationFn: (a: { id: string; archived: boolean }) => setFundArchived(a.id, a.archived) })

  const run = async (fn: () => Promise<unknown>) => {
    setError('')
    try {
      await fn()
      await refresh()
      toast.success('Fund saved.')
    } catch (e) {
      setError(errText(e))
    }
  }
  const onAdd = (e: FormEvent) => {
    e.preventDefault()
    void run(async () => {
      await add.mutateAsync()
      setName('')
    })
  }

  return (
    <Sheet isOpen={isOpen} onClose={onClose} title="Funds">
      <div className="flex flex-col gap-5">
        <p className="text-body text-muted">
          A fund is a place money is counted into, such as Tithe or a special offering. Retiring a fund hides it from new counts; what was counted stays on record.
        </p>
        <ul className="divide-y divide-line" aria-label="Funds">
          {funds.map((f) => (
            <li key={f.id} className="py-3">
              {renaming?.id === f.id ? (
                <form
                  className="flex flex-wrap items-end gap-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    void run(async () => {
                      await rename.mutateAsync(renaming)
                      setRenaming(null)
                    })
                  }}
                >
                  <div className="min-w-0 flex-1"><Input label="Name" value={renaming.name} onChange={(e) => setRenaming({ id: f.id, name: e.target.value })} /></div>
                  <Button type="submit" size="lg" isLoading={rename.isPending}>Save</Button>
                  <Button type="button" variant="secondary" size="lg" onClick={() => setRenaming(null)}>Cancel</Button>
                </form>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{f.name}</p>
                    <p className="text-body text-muted">{KIND_LABELS[f.kind]} · {DESTINATION_LABELS[f.destination]}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {f.archived && <Badge tone="neutral">Retired</Badge>}
                    <Button type="button" variant="secondary" size="lg" onClick={() => setRenaming({ id: f.id, name: f.name })}>Rename</Button>
                    {f.kind !== 'tithe' && (
                      <Button type="button" variant="secondary" size="lg" onClick={() => void run(() => archive.mutateAsync({ id: f.id, archived: !f.archived }))}>
                        {f.archived ? 'Bring back' : 'Retire'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>

        <form onSubmit={onAdd} className="flex flex-col gap-3 rounded-control border border-line p-3" noValidate>
          <h4 className="font-semibold text-ink">Add a fund</h4>
          <Input label="Name" placeholder="Harvest appeal" value={name} onChange={(e) => setName(e.target.value)} />
          <Select
            label="What kind of money is it?"
            value={kind}
            onChange={(e) => setKind(e.target.value as Exclude<IncomeKind, 'tithe'>)}
            options={NEW_KINDS.map((k) => ({ value: k, label: KIND_LABELS[k] }))}
          />
          <Select
            label="Where does it go?"
            value={destination}
            onChange={(e) => setDestination(e.target.value as IncomeDestination)}
            options={(Object.keys(DESTINATION_LABELS) as IncomeDestination[]).map((d) => ({ value: d, label: DESTINATION_LABELS[d] }))}
            hint="This cannot be changed once the fund is used."
          />
          <Button type="submit" size="lg" isLoading={add.isPending}>Add the fund</Button>
        </form>
        {error && <p role="alert" className="text-body text-bad">{error}</p>}
      </div>
    </Sheet>
  )
}
